import React, { createContext, useCallback, useContext, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

const HoverPopoutContext = createContext(false);

// The hover overlay is rendered (via portal) into document.body as position:fixed,
// so it sits OUTSIDE the sidebar's scroll container. A wheel over it would try to
// scroll the page body instead of the sidebar, so scrolling appeared frozen.
// Walk up from the anchor to find the actual scrollable sidebar element.
function getScrollableAncestor(node) {
    let el = node ? node.parentElement : null;
    while (el && el !== document.body && el !== document.documentElement) {
        const { overflowY } = window.getComputedStyle(el);
        if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) {
            return el;
        }
        el = el.parentElement;
    }
    return null;
}

// Briefly ignore re-hover right after a wheel so the popout doesn't flicker back
// open while the user is actively scrolling. Shared across rows.
let suppressHoverUntil = 0;

function HoverPopout({ children }) {
    const anchorRef = useRef(null);
    const overlayRef = useRef(null);
    const [hovered, setHovered] = useState(false);
    const [rect, setRect] = useState(null);

    const getRelatedNode = useCallback((event) => {
        const { relatedTarget } = event;

        if (!relatedTarget || typeof Node === 'undefined' || !(relatedTarget instanceof Node)) {
            return null;
        }

        return relatedTarget;
    }, []);

    const updateRect = useCallback(() => {
        if (!anchorRef.current) {
            return;
        }

        const nextRect = anchorRef.current.getBoundingClientRect();
        setRect({
            left: nextRect.left,
            top: nextRect.top,
            width: nextRect.width,
            height: nextRect.height,
        });
    }, []);

    const updateHover = useCallback((nextHovered) => {
        if (nextHovered && performance.now() < suppressHoverUntil) {
            return;
        }
        setHovered(nextHovered);
        if (nextHovered) {
            updateRect();
        }
    }, [updateRect]);

    useLayoutEffect(() => {
        if (!hovered) {
            return undefined;
        }

        const handlePositionChange = () => updateRect();
        handlePositionChange();

        window.addEventListener('scroll', handlePositionChange, true);
        window.addEventListener('resize', handlePositionChange);

        return () => {
            window.removeEventListener('scroll', handlePositionChange, true);
            window.removeEventListener('resize', handlePositionChange);
        };
    }, [hovered, updateRect]);

    // When the wheel is spun over the hover overlay, forward the scroll to the
    // sidebar and close the popout, so the user can scroll down instead of the
    // overlay swallowing the gesture. Native (passive:false) so preventDefault
    // stops any competing page-body scroll.
    useLayoutEffect(() => {
        if (!hovered) {
            return undefined;
        }

        const node = overlayRef.current;
        if (!node) {
            return undefined;
        }

        const handleWheel = (event) => {
            const scroller = getScrollableAncestor(anchorRef.current);
            if (scroller) {
                let delta = event.deltaY;
                if (event.deltaMode === 1) {
                    delta *= 16;                    // deltaMode "lines" -> approx px
                } else if (event.deltaMode === 2) {
                    delta *= scroller.clientHeight;  // deltaMode "pages"
                }
                scroller.scrollTop += delta;
                event.preventDefault();
            }
            suppressHoverUntil = performance.now() + 200;
            setHovered(false);
        };

        node.addEventListener('wheel', handleWheel, { passive: false });
        return () => node.removeEventListener('wheel', handleWheel);
    }, [hovered]);

    const handleAnchorMouseLeave = useCallback((event) => {
        const relatedNode = getRelatedNode(event);

        if (relatedNode && overlayRef.current && overlayRef.current.contains(relatedNode)) {
            return;
        }

        updateHover(false);
    }, [getRelatedNode, updateHover]);

    const handleOverlayMouseLeave = useCallback((event) => {
        const relatedNode = getRelatedNode(event);

        if (relatedNode && anchorRef.current && anchorRef.current.contains(relatedNode)) {
            return;
        }

        updateHover(false);
    }, [getRelatedNode, updateHover]);

    const overlay = hovered && rect
        ? ReactDOM.createPortal(
            <div
                ref={overlayRef}
                className="hover-popout-surface"
                style={{
                    position: 'fixed',
                    left: rect.left,
                    top: rect.top,
                    zIndex: 1800,
                    height: '35px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
                onMouseEnter={() => updateHover(true)}
                onMouseLeave={handleOverlayMouseLeave}
            >
                <HoverPopoutContext.Provider value={true}>
                    {children}
                </HoverPopoutContext.Provider>
            </div>,
            document.body
        )
        : null;

    return (
        <>
            <div
                ref={anchorRef}
                className="hover-popout-anchor"
                style={{
                    opacity: hovered ? 0 : 1,
                }}
                onMouseEnter={() => updateHover(true)}
                onMouseLeave={handleAnchorMouseLeave}
            >
                <HoverPopoutContext.Provider value={false}>
                    {children}
                </HoverPopoutContext.Provider>
            </div>
            {overlay}
        </>
    );
}

function HoverPopoutActions({ children }) {
    const showActions = useContext(HoverPopoutContext);
    return showActions ? <>{children}</> : null;
}

HoverPopout.Actions = HoverPopoutActions;

export default HoverPopout;